import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Calendar, CreditCard,
  FileText, Package, Wifi, AlertCircle, DollarSign,
  Edit, ArrowLeft, Clock, CheckCircle, XCircle,
  HeadphonesIcon, MessageSquare, Activity,
  TrendingUp, Shield, FileCheck, ListChecks, CheckSquare,
  Plus, Trash2, X as XIcon
} from 'lucide-react';
import api from '../../services/api';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Tab, TabList, TabPanel, Tabs } from '../../components/UI/Tabs';
import { usePermissions } from '../../hooks/usePermissions';

const ClienteDetalhesCompleto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit, canView } = usePermissions();
  
  // Verifica de onde veio a navegação
  const fromAtendimento = location.state?.fromAtendimento;
  const fromChamado = location.state?.fromChamado;
  const fromOrdemServico = location.state?.fromOrdemServico;
  const fromTarefa = location.state?.fromTarefa;
  const referenceId = location.state?.referenceId;
  const referenceType = location.state?.type;
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Dados do cliente
  const [cliente, setCliente] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [boletos, setBoletos] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [chamados, setChamados] = useState([]);
  const [atendimentos, setAtendimentos] = useState([]);
  const [ordensServico, setOrdensServico] = useState([]);
  
  // Dados de referência (atendimento/chamado/OS atual)
  const [referenceData, setReferenceData] = useState(null);
  
  // Modal de Checklist
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedAtendimento, setSelectedAtendimento] = useState(null);
  const [selectedChamado, setSelectedChamado] = useState(null);
  const [selectedOS, setSelectedOS] = useState(null);
  const [checklistType, setChecklistType] = useState(null); // 'atendimento', 'chamado' ou 'ordem_servico'
  const [checklist, setChecklist] = useState([]);
  const [checklistInput, setChecklistInput] = useState('');
  const [checklistHerdado, setChecklistHerdado] = useState([]);
  
  // Estado para checklist inline
  const [checklistInlineModificado, setChecklistInlineModificado] = useState(false);
  const [checklistInlineOriginal, setChecklistInlineOriginal] = useState([]);
  
  // Estatísticas
  const [stats, setStats] = useState({
    totalGasto: 0,
    ticketMedio: 0,
    tempoCliente: 0,
    inadimplencia: 0,
    chamadosAbertos: 0,
    satisfacao: 0
  });

  useEffect(() => {
    fetchAllData();
  }, [id, referenceId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Busca dados principais do cliente
      const [
        clienteRes,
        contratosRes,
        boletosRes,
        equipamentosRes,
        chamadosRes,
        atendimentosRes,
        ordensRes
      ] = await Promise.all([
        api.get(`/clientes/${id}`),
        api.get(`/contratos/cliente/${id}`),
        api.get(`/boletos/cliente/${id}`),
        api.get(`/equipamentos/cliente/${id}`),
        api.get(`/chamados/cliente/${id}`),
        api.get(`/atendimentos/cliente/${id}`),
        api.get(`/ordens-servico/cliente/${id}`)
      ]);

      setCliente(clienteRes.data);
      setContratos(contratosRes.data);
      setBoletos(boletosRes.data);
      setEquipamentos(equipamentosRes.data);
      setChamados(chamadosRes.data);
      setAtendimentos(atendimentosRes.data);
      setOrdensServico(ordensRes.data);

      // Calcula estatísticas
      calculateStats(boletosRes.data, clienteRes.data, chamadosRes.data);

      // Busca dados de referência se houver
      if (referenceId && referenceType) {
        fetchReferenceData();
      }
    } catch (error) {
      toast.error('Erro ao carregar dados do cliente');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      let endpoint = '';
      switch (referenceType) {
        case 'atendimento':
          endpoint = `/atendimentos/${referenceId}`;
          break;
        case 'chamado':
          endpoint = `/chamados/${referenceId}`;
          break;
        case 'ordem_servico':
          endpoint = `/ordens-servico/${referenceId}`;
          break;
      }
      
      if (endpoint) {
        const response = await api.get(endpoint);
        setReferenceData(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de referência:', error);
    }
  };

  const calculateStats = (boletosData, clienteData, chamadosData) => {
    const totalGasto = boletosData
      .filter(b => b.status === 'pago')
      .reduce((sum, b) => sum + b.valor, 0);
    
    const ticketMedio = boletosData.length > 0 
      ? totalGasto / boletosData.filter(b => b.status === 'pago').length 
      : 0;
    
    const tempoCliente = differenceInDays(
      new Date(),
      new Date(clienteData.createdAt)
    );
    
    const boletosVencidos = boletosData.filter(b => b.status === 'vencido').length;
    const inadimplencia = boletosData.length > 0 
      ? (boletosVencidos / boletosData.length) * 100 
      : 0;
    
    const chamadosAbertos = chamadosData.filter(
      c => c.status === 'aberto' || c.status === 'em_andamento'
    ).length;

    setStats({
      totalGasto,
      ticketMedio,
      tempoCliente,
      inadimplencia,
      chamadosAbertos,
      satisfacao: 4.5 // Poderia vir de uma API de avaliação
    });
  };

  // Funções de gerenciamento de checklist
  const handleOpenChecklist = async (item, type = 'atendimento') => {
    try {
      setChecklistType(type);
      
      if (type === 'atendimento') {
        setSelectedAtendimento(item);
        setSelectedChamado(null);
        setSelectedOS(null);
        
        // Buscar detalhes do atendimento
        const responseAtend = await api.get(`/atendimentos/${item.id}`);
        const atendimentoData = responseAtend.data;
        
        // Inicializar checklist do atendimento
        setChecklist(atendimentoData.checklist || []);
        
        // Buscar checklist do tipo para identificar itens herdados
        if (atendimentoData.tipoId) {
          const responseTipo = await api.get(`/atendimentos-tipos/${atendimentoData.tipoId}`);
          const tipoData = responseTipo.data;
          setChecklistHerdado(tipoData.checklist || []);
        } else {
          setChecklistHerdado([]);
        }
      } else if (type === 'chamado') {
        setSelectedChamado(item);
        setSelectedAtendimento(null);
        setSelectedOS(null);
        
        // Buscar detalhes do chamado
        const responseChamado = await api.get(`/chamados/${item.id}`);
        const chamadoData = responseChamado.data;
        
        // Inicializar checklist do chamado
        setChecklist(chamadoData.checklist || []);
        
        // Buscar checklist do tipo para identificar itens herdados
        if (chamadoData.tipoId) {
          const responseTipo = await api.get(`/chamados-tipos/${chamadoData.tipoId}`);
          const tipoData = responseTipo.data;
          setChecklistHerdado(tipoData.checklist || []);
        } else {
          setChecklistHerdado([]);
        }
      } else if (type === 'ordem_servico') {
        setSelectedOS(item);
        setSelectedAtendimento(null);
        setSelectedChamado(null);
        
        // Buscar detalhes da ordem de serviço
        const responseOS = await api.get(`/ordens-servico/${item.id}`);
        const osData = responseOS.data;
        
        // Inicializar checklist da OS
        setChecklist(osData.checklist || []);
        setChecklistHerdado([]);
      }
      
      setShowChecklistModal(true);
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      toast.error('Erro ao carregar checklist');
    }
  };

  const handleCloseChecklist = () => {
    setShowChecklistModal(false);
    setSelectedAtendimento(null);
    setSelectedChamado(null);
    setSelectedOS(null);
    setChecklistType(null);
    setChecklist([]);
    setChecklistInput('');
    setChecklistHerdado([]);
  };

  const handleAddChecklistItem = () => {
    if (!checklistInput.trim()) {
      toast.error('Digite um item válido');
      return;
    }

    const novoItem = {
      item: checklistInput.trim(),
      concluido: false
    };

    setChecklist([...checklist, novoItem]);
    setChecklistInput('');
  };

  const handleRemoveChecklistItem = (index) => {
    const itemRemover = checklist[index];
    const ehItemHerdado = checklistHerdado.some(h => h.item === itemRemover.item);

    if (ehItemHerdado) {
      if (!window.confirm('Este item foi herdado do tipo de atendimento. Deseja realmente removê-lo?')) {
        return;
      }
    }

    const novaChecklist = checklist.filter((_, i) => i !== index);
    setChecklist(novaChecklist);
  };

  const handleToggleChecklistItem = (index) => {
    const novaChecklist = [...checklist];
    novaChecklist[index] = {
      ...novaChecklist[index],
      concluido: !novaChecklist[index].concluido
    };
    setChecklist(novaChecklist);
  };

  const handleSaveChecklist = async () => {
    try {
      const totalItens = checklist.length;
      const itensConcluidos = checklist.filter(item => item.concluido).length;
      const progressoPercentual = totalItens > 0 ? Math.round((itensConcluidos / totalItens) * 100) : 0;

      if (checklistType === 'atendimento') {
        await api.patch(`/atendimentos/${selectedAtendimento.id}`, {
          checklist: checklist.length > 0 ? checklist : null,
          progressoPercentual
        });

        toast.success('Checklist do atendimento atualizado com sucesso!');
        
        // Recarregar atendimentos
        const response = await api.get(`/atendimentos/cliente/${id}`);
        setAtendimentos(response.data);
      } else if (checklistType === 'chamado') {
        await api.patch(`/chamados/${selectedChamado.id}`, {
          checklist: checklist.length > 0 ? checklist : null,
          progressoPercentual
        });

        toast.success('Checklist do chamado atualizado com sucesso!');
        
        // Recarregar chamados
        const response = await api.get(`/chamados/cliente/${id}`);
        setChamados(response.data);
      } else if (checklistType === 'ordem_servico') {
        await api.patch(`/ordens-servico/${selectedOS.id}`, {
          checklist: checklist.length > 0 ? checklist : null,
          progressoPercentual
        });

        toast.success('Checklist da ordem de serviço atualizado com sucesso!');
        
        // Recarregar ordens de serviço
        const response = await api.get(`/ordens-servico/cliente/${id}`);
        setOrdensServico(response.data);
      }

      handleCloseChecklist();
      
      // Atualizar referência se for o item atual
      if (referenceId) {
        fetchReferenceData();
      }
    } catch (error) {
      console.error('Erro ao salvar checklist:', error);
      toast.error('Erro ao salvar checklist');
    }
  };

  // Função para toggle de item inline
  const handleToggleChecklistInline = (index) => {
    const novoChecklist = [...referenceData.checklist];
    novoChecklist[index] = {
      ...novoChecklist[index],
      concluido: !novoChecklist[index].concluido
    };
    
    // Atualizar referenceData com novo checklist
    setReferenceData({
      ...referenceData,
      checklist: novoChecklist
    });
    
    // Marcar como modificado
    if (!checklistInlineModificado) {
      setChecklistInlineOriginal([...referenceData.checklist]);
    }
    setChecklistInlineModificado(true);
  };

  // Função para salvar checklist inline
  const handleSaveChecklistInline = async () => {
    try {
      const itensConcluidos = referenceData.checklist.filter(item => item.concluido).length;
      const totalItens = referenceData.checklist.length;
      const progressoPercentual = totalItens > 0 
        ? Math.round((itensConcluidos / totalItens) * 100) 
        : 0;

      const endpoint = referenceType === 'atendimento' 
        ? `/atendimentos/${referenceData.id}` 
        : referenceType === 'chamado'
        ? `/chamados/${referenceData.id}`
        : `/ordens-servico/${referenceData.id}`;

      await api.patch(endpoint, {
        checklist: referenceData.checklist,
        progressoPercentual
      });

      // Atualizar progressoPercentual no referenceData
      setReferenceData({
        ...referenceData,
        progressoPercentual
      });

      toast.success('Checklist atualizado com sucesso!');
      setChecklistInlineModificado(false);
      setChecklistInlineOriginal([]);
      
      // Recarregar dados
      fetchAllData();
    } catch (error) {
      console.error('Erro ao salvar checklist:', error);
      toast.error('Erro ao salvar checklist');
    }
  };

  // Função para cancelar alterações inline
  const handleCancelChecklistInline = () => {
    setReferenceData({
      ...referenceData,
      checklist: checklistInlineOriginal
    });
    setChecklistInlineModificado(false);
    setChecklistInlineOriginal([]);
  };

  const getStatusColor = (status) => {
    const colors = {
      'ativo': 'bg-green-100 text-green-800',
      'suspenso': 'bg-yellow-100 text-yellow-800',
      'cancelado': 'bg-red-100 text-red-800',
      'pendente': 'bg-orange-100 text-orange-800',
      'pago': 'bg-green-100 text-green-800',
      'vencido': 'bg-red-100 text-red-800',
      'aberto': 'bg-blue-100 text-blue-800',
      'em_andamento': 'bg-yellow-100 text-yellow-800',
      'resolvido': 'bg-green-100 text-green-800',
      'finalizada': 'bg-green-100 text-green-800',
      'agendada': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleNewAtendimento = () => {
    navigate('/atendimentos/novo', { 
      state: { clienteId: id, clienteNome: cliente.nome } 
    });
  };

  const handleNewChamado = () => {
    navigate('/chamados/novo', { 
      state: { clienteId: id, clienteNome: cliente.nome } 
    });
  };

  const handleNewOrdemServico = () => {
    navigate('/ordens-servico/novo', { 
      state: { clienteId: id, clienteNome: cliente.nome } 
    });
  };

  const handleNewTarefa = () => {
    navigate('/tarefas/novo', { 
      state: { 
        clienteId: id, 
        clienteNome: cliente.nome,
        ...(referenceType === 'atendimento' && { atendimentoId: referenceId }),
        ...(referenceType === 'chamado' && { chamadoId: referenceId }),
        ...(referenceType === 'ordem_servico' && { ordemServicoId: referenceId })
      } 
    });
  };

  // Determina quais botões mostrar baseado na origem
  const shouldShowButton = (buttonType) => {
    if (referenceType === 'atendimento') {
      return ['chamado', 'os', 'tarefa'].includes(buttonType);
    }
    if (referenceType === 'chamado') {
      return ['atendimento', 'os', 'tarefa'].includes(buttonType);
    }
    if (referenceType === 'ordem_servico') {
      return ['atendimento', 'chamado', 'tarefa'].includes(buttonType);
    }
    if (referenceType === 'tarefa') {
      return ['atendimento', 'chamado', 'os'].includes(buttonType);
    }
    // Se não veio de nenhum contexto, mostra todos
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!cliente) return null;

  return (
    <div className="space-y-6">
      {/* Header com contexto */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cliente.nome}
                </h1>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  cliente.ativo 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {cliente.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              
              <div className="flex items-center gap-6 mt-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  {cliente.tipoPessoa === 'F' ? 'CPF' : 'CNPJ'}: {cliente.cpfCnpj}
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {cliente.email}
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {cliente.telefones?.[0]?.numero || 'Sem telefone'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {canEdit('clientes') && (
              <button
                onClick={() => navigate(`/clientes/${id}/editar`)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
            )}
          </div>
        </div>
        
        {/* Ações Rápidas - Contextuais baseadas na origem */}
        <div className="flex gap-2 mt-4 pt-4 border-t">
          {shouldShowButton('atendimento') && (
            <button
              onClick={handleNewAtendimento}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <HeadphonesIcon className="w-4 h-4" />
              Novo Atendimento
            </button>
          )}
          
          {shouldShowButton('chamado') && (
            <button
              onClick={handleNewChamado}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4" />
              Abrir Chamado
            </button>
          )}
          
          {shouldShowButton('os') && (
            <button
              onClick={handleNewOrdemServico}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Nova O.S.
            </button>
          )}
          
          {shouldShowButton('tarefa') && (
            <button
              onClick={handleNewTarefa}
              className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Nova Tarefa
            </button>
          )}
        </div>
      </div>

      {/* Contexto de Referência (Atendimento/Chamado/OS atual) */}
      {referenceData && (
        <div className={`rounded-lg p-4 ${
          referenceType === 'atendimento' ? 'bg-green-50 dark:bg-green-900/20' :
          referenceType === 'chamado' ? 'bg-blue-50 dark:bg-blue-900/20' :
          referenceType === 'ordem_servico' ? 'bg-purple-50 dark:bg-purple-900/20' :
          'bg-orange-50 dark:bg-orange-900/20'
        }`}>
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            {referenceType === 'atendimento' && <HeadphonesIcon className="w-5 h-5" />}
            {referenceType === 'chamado' && <AlertCircle className="w-5 h-5" />}
            {referenceType === 'ordem_servico' && <Shield className="w-5 h-5" />}
            {referenceType === 'tarefa' && <CheckCircle className="w-5 h-5" />}
            {referenceType === 'atendimento' ? 'Atendimento Atual' :
             referenceType === 'chamado' ? 'Chamado Atual' :
             referenceType === 'ordem_servico' ? 'Ordem de Serviço Atual' :
             referenceType === 'tarefa' ? 'Tarefa Atual' : ''}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {referenceType === 'atendimento' && (
              <>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Tipo:</span>
                  <span className="ml-2 font-medium">{referenceData.tipo?.nome}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Canal:</span>
                  <span className="ml-2 font-medium">{referenceData.canal}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Duração:</span>
                  <span className="ml-2 font-medium">{referenceData.duracaoMinutos} min</span>
                </div>
              </>
            )}
            
            {referenceType === 'chamado' && (
              <>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Protocolo:</span>
                  <span className="ml-2 font-medium">#{referenceData.protocolo}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(referenceData.status)}`}>
                    {referenceData.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Prioridade:</span>
                  <span className="ml-2 font-medium">{referenceData.prioridade}</span>
                </div>
              </>
            )}
            
            {referenceType === 'ordem_servico' && (
              <>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Protocolo:</span>
                  <span className="ml-2 font-medium">{referenceData.protocolo}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Tipo:</span>
                  <span className="ml-2 font-medium">{referenceData.tipo}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(referenceData.status)}`}>
                    {referenceData.status}
                  </span>
                </div>
              </>
            )}
            
            {referenceType === 'tarefa' && (
              <>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Título:</span>
                  <span className="ml-2 font-medium">{referenceData.titulo}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Prioridade:</span>
                  <span className="ml-2 font-medium">{referenceData.prioridade}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(referenceData.status)}`}>
                    {referenceData.status}
                  </span>
                </div>
              </>
            )}
          </div>
          
          {referenceData.descricao && (
            <div className="mt-3 p-3 bg-white dark:bg-dark-card rounded">
              <p className="text-sm font-medium mb-1">Descrição:</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {referenceData.descricao}
              </p>
            </div>
          )}
          
          {/* Checklist do Atendimento/Chamado/OS */}
          {(referenceType === 'atendimento' || referenceType === 'chamado' || referenceType === 'ordem_servico') && referenceData.checklist && referenceData.checklist.length > 0 && (
            <div className="mt-3 p-3 bg-white dark:bg-dark-card rounded">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  <ListChecks className="w-4 h-4" />
                  Checklist
                </p>
                <div className="flex items-center gap-2">
                  {checklistInlineModificado && (
                    <>
                      <button
                        onClick={handleCancelChecklistInline}
                        className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSaveChecklistInline}
                        className="text-xs text-white bg-primary-600 hover:bg-primary-700 px-2 py-1 rounded flex items-center gap-1"
                      >
                        <CheckSquare className="w-3 h-3" />
                        Salvar
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleOpenChecklist(referenceData, referenceType)}
                    className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                  >
                    <CheckSquare className="w-3 h-3" />
                    Gerenciar
                  </button>
                </div>
              </div>
              
              {/* Barra de Progresso */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Progresso</span>
                  <span className="text-xs font-semibold text-primary-600">
                    {referenceData.checklist.filter(item => item.concluido).length} de {referenceData.checklist.length} ({
                      referenceData.checklist.length > 0 
                        ? Math.round((referenceData.checklist.filter(item => item.concluido).length / referenceData.checklist.length) * 100)
                        : 0
                    }%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${
                        referenceData.checklist.length > 0 
                          ? Math.round((referenceData.checklist.filter(item => item.concluido).length / referenceData.checklist.length) * 100)
                          : 0
                      }%` 
                    }}
                  />
                </div>
              </div>
              
              {/* Lista de Itens do Checklist */}
              <div className="space-y-2">
                {referenceData.checklist.slice(0, 5).map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded transition-colors"
                    onClick={() => handleToggleChecklistInline(index)}
                  >
                    <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${
                      item.concluido 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-gray-300 hover:border-primary-500'
                    }`}>
                      {item.concluido && (
                        <CheckSquare className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className={`flex-1 ${item.concluido ? 'line-through text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                      {item.item}
                    </span>
                  </div>
                ))}
                
                {referenceData.checklist.length > 5 && (
                  <p className="text-xs text-gray-500 mt-2">
                    +{referenceData.checklist.length - 5} itens restantes (clique em "Gerenciar" para ver todos)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            R$ {stats.totalGasto.toLocaleString('pt-BR')}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Total gasto</p>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <span className="text-xs text-gray-500">Média</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            R$ {stats.ticketMedio.toFixed(2)}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Ticket médio</p>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            <span className="text-xs text-gray-500">Tempo</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats.tempoCliente} dias
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Como cliente</p>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <CreditCard className="w-5 h-5 text-red-500" />
            <span className="text-xs text-gray-500">Inadim.</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats.inadimplencia.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Inadimplência</p>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <span className="text-xs text-gray-500">Chamados</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats.chamadosAbertos}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Em aberto</p>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-yellow-500" />
            <span className="text-xs text-gray-500">Satisfação</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats.satisfacao.toFixed(1)} ⭐
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Avaliação</p>
        </div>
      </div>

      {/* Informações Detalhadas em Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabList>
          <Tab>Dados Pessoais</Tab>
          <Tab>Contratos ({contratos.length})</Tab>
          <Tab>Faturas ({boletos.length})</Tab>
          <Tab>Equipamentos ({equipamentos.length})</Tab>
          <Tab>Chamados ({chamados.length})</Tab>
          <Tab>Atendimentos ({atendimentos.length})</Tab>
          <Tab>O.S. ({ordensServico.length})</Tab>
        </TabList>

        {/* Tab de Dados Pessoais */}
        <TabPanel index={0}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Informações Básicas */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Informações Pessoais
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Nome Completo</p>
                  <p className="font-medium">{cliente.nome}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cliente.tipoPessoa === 'F' ? 'CPF' : 'CNPJ'}
                  </p>
                  <p className="font-medium">{cliente.cpfCnpj}</p>
                </div>
                {cliente.rgIe && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {cliente.tipoPessoa === 'F' ? 'RG' : 'IE'}
                    </p>
                    <p className="font-medium">{cliente.rgIe}</p>
                  </div>
                )}
                {cliente.dataNascimento && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Data de Nascimento</p>
                    <p className="font-medium">
                      {format(new Date(cliente.dataNascimento), 'dd/MM/yyyy')}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Cliente desde</p>
                  <p className="font-medium">
                    {format(new Date(cliente.createdAt), 'dd/MM/yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Dia de Vencimento</p>
                  <p className="font-medium">Dia {cliente.diaVencimento}</p>
                </div>
              </div>
            </div>

            {/* Contatos */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contatos
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                  <p className="font-medium">{cliente.email}</p>
                </div>
                {cliente.telefones?.map((tel, index) => (
                  <div key={index}>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {tel.tipo} {tel.principal && '(Principal)'}
                    </p>
                    <p className="font-medium flex items-center gap-2">
                      {tel.numero}
                      {tel.whatsapp && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          WhatsApp
                        </span>
                      )}
                    </p>
                    {tel.contato && (
                      <p className="text-xs text-gray-500">Contato: {tel.contato}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Endereços */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Endereços
              </h3>
              <div className="space-y-3">
                {cliente.enderecos?.map((end, index) => (
                  <div key={index} className="pb-3 border-b last:border-0">
                    <p className="text-xs text-primary-600 uppercase font-medium">
                      {end.tipo}
                    </p>
                    <p className="text-sm mt-1">
                      {end.logradouro}, {end.numero}
                      {end.complemento && ` - ${end.complemento}`}
                    </p>
                    <p className="text-sm">
                      {end.bairro}
                    </p>
                    <p className="text-sm">
                      {end.cidade?.nome}/{end.cidade?.estado} - CEP: {end.cep}
                    </p>
                    {end.referencia && (
                      <p className="text-xs text-gray-500 mt-1">
                        Ref: {end.referencia}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Observações */}
          {cliente.observacoes && (
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mt-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Observações
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {cliente.observacoes}
              </p>
            </div>
          )}
        </TabPanel>

        {/* Tab de Contratos */}
        <TabPanel index={1}>
          <ContratosList contratos={contratos} clienteId={id} />
        </TabPanel>

        {/* Tab de Faturas */}
        <TabPanel index={2}>
          <BoletosList boletos={boletos} clienteId={id} />
        </TabPanel>

        {/* Tab de Equipamentos */}
        <TabPanel index={3}>
          <EquipamentosList equipamentos={equipamentos} clienteId={id} />
        </TabPanel>

        {/* Tab de Chamados */}
        <TabPanel index={4}>
          <ChamadosList 
            chamados={chamados} 
            clienteId={id}
            onOpenChecklist={handleOpenChecklist}
          />
        </TabPanel>

        {/* Tab de Atendimentos */}
        <TabPanel index={5}>
          <AtendimentosList 
            atendimentos={atendimentos} 
            clienteId={id} 
            onOpenChecklist={handleOpenChecklist}
          />
        </TabPanel>

        {/* Tab de Ordens de Serviço */}
        <TabPanel index={6}>
          <OrdensServicoList 
            ordensServico={ordensServico} 
            clienteId={id}
            onOpenChecklist={handleOpenChecklist}
          />
        </TabPanel>
      </Tabs>

      {/* Modal de Checklist */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold">Gerenciar Checklist</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {checklistType === 'atendimento' 
                    ? selectedAtendimento?.tipo?.nome 
                    : checklistType === 'chamado'
                    ? `Chamado: ${selectedChamado?.titulo}`
                    : checklistType === 'ordem_servico'
                    ? `O.S.: ${selectedOS?.protocolo} - ${selectedOS?.titulo}`
                    : ''}
                </p>
              </div>
              <button
                onClick={handleCloseChecklist}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Progresso */}
              {checklist.length > 0 && (
                <div className="mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Progresso</span>
                    <span className="text-sm font-semibold text-primary-600">
                      {checklist.filter(item => item.concluido).length} de {checklist.length} concluídos
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${checklist.length > 0 
                          ? Math.round((checklist.filter(item => item.concluido).length / checklist.length) * 100) 
                          : 0}%` 
                      }}
                    />
                  </div>
                  <p className="text-center text-sm font-medium text-gray-700 dark:text-gray-300 mt-2">
                    {checklist.length > 0 
                      ? Math.round((checklist.filter(item => item.concluido).length / checklist.length) * 100) 
                      : 0}%
                  </p>
                </div>
              )}

              {/* Lista de Itens */}
              <div className="space-y-2 mb-4">
                {checklist.map((item, index) => {
                  const ehItemHerdado = checklistHerdado.some(h => h.item === item.item);
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        item.concluido
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                          : 'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                      }`}
                    >
                      <button
                        onClick={() => handleToggleChecklistItem(index)}
                        className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          item.concluido
                            ? 'bg-green-500 border-green-500'
                            : 'border-gray-300 hover:border-primary-500'
                        }`}
                      >
                        {item.concluido && (
                          <CheckSquare className="w-4 h-4 text-white" />
                        )}
                      </button>
                      
                      <span
                        className={`flex-1 ${
                          item.concluido
                            ? 'line-through text-gray-500'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {item.item}
                        {ehItemHerdado && (
                          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                            (herdado)
                          </span>
                        )}
                      </span>
                      
                      <button
                        onClick={() => handleRemoveChecklistItem(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                        title="Remover item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}

                {checklist.length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    Nenhum item no checklist. Adicione itens abaixo.
                  </p>
                )}
              </div>

              {/* Adicionar Novo Item */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={checklistInput}
                  onChange={(e) => setChecklistInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                  placeholder="Digite um novo item..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600"
                />
                <button
                  onClick={handleAddChecklistItem}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={handleCloseChecklist}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveChecklist}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componentes auxiliares para as listas nas tabs
const ContratosList = ({ contratos, clienteId }) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Contratos</h3>
          <button
            onClick={() => navigate('/contratos/novo', { state: { clienteId } })}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Novo Contrato
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Plano</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Valor</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Início</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {contratos.map((contrato) => (
              <tr key={contrato.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3">
                  <p className="font-medium">{contrato.plano?.nomePlano}</p>
                  <p className="text-xs text-gray-500">
                    {contrato.plano?.limitDownload}/{contrato.plano?.limitUpload} MB
                  </p>
                </td>
                <td className="px-4 py-3">
                  <p>R$ {contrato.valorMensalidade}</p>
                  {contrato.desconto > 0 && (
                    <p className="text-xs text-green-600">
                      Desconto: R$ {contrato.desconto}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    contrato.status === 'ativo' ? 'bg-green-100 text-green-800' :
                    contrato.status === 'suspenso' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {contrato.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {format(new Date(contrato.dataInicio), 'dd/MM/yyyy')}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => navigate(`/contratos/${contrato.id}`)}
                    className="text-primary-600 hover:text-primary-800 text-sm"
                  >
                    Ver detalhes
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {contratos.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhum contrato encontrado
          </div>
        )}
      </div>
    </div>
  );
};

const BoletosList = ({ boletos, clienteId }) => {
  const navigate = useNavigate();
  
  const getStatusColor = (status) => {
    const colors = {
      'pendente': 'bg-orange-100 text-orange-800',
      'pago': 'bg-green-100 text-green-800',
      'vencido': 'bg-red-100 text-red-800',
      'cancelado': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold">Histórico de Faturas</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Nosso Número</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Valor</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Vencimento</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {boletos.slice(0, 10).map((boleto) => (
              <tr key={boleto.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 text-sm">{boleto.nossoNumero}</td>
                <td className="px-4 py-3">
                  <p className="font-medium">R$ {boleto.valor}</p>
                  {boleto.valorPago && (
                    <p className="text-xs text-gray-500">
                      Pago: R$ {boleto.valorPago}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {format(new Date(boleto.dataVencimento), 'dd/MM/yyyy')}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(boleto.status)}`}>
                    {boleto.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => navigate(`/boletos/${boleto.id}`)}
                    className="text-primary-600 hover:text-primary-800 text-sm"
                  >
                    Ver detalhes
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {boletos.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhuma fatura encontrada
          </div>
        )}
        
        {boletos.length > 10 && (
          <div className="p-4 text-center border-t">
            <button
              onClick={() => navigate(`/boletos?clienteId=${clienteId}`)}
              className="text-primary-600 hover:text-primary-800 text-sm"
            >
              Ver todas as {boletos.length} faturas
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const EquipamentosList = ({ equipamentos, clienteId }) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold">Equipamentos Instalados</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
        {equipamentos.map((equip) => (
          <div key={equip.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-2">
              <Wifi className="w-5 h-5 text-primary-500" />
              <span className={`text-xs px-2 py-1 rounded-full ${
                equip.status === 'instalado' ? 'bg-green-100 text-green-800' :
                equip.status === 'manutencao' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {equip.status}
              </span>
            </div>
            
            <p className="font-medium">{equip.tipo?.nome}</p>
            <p className="text-sm text-gray-600 mt-2">
              <span className="font-medium">Modelo:</span> {equip.modelo}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">MAC:</span> {equip.macAddress}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Serial:</span> {equip.serial}
            </p>
            
            {equip.ipAddress && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">IP:</span> {equip.ipAddress}
              </p>
            )}
            
            {equip.dataInstalacao && (
              <p className="text-sm text-gray-600 mt-2">
                Instalado em: {format(new Date(equip.dataInstalacao), 'dd/MM/yyyy')}
              </p>
            )}
            
            <button
              onClick={() => navigate(`/equipamentos/${equip.id}`)}
              className="mt-3 text-primary-600 hover:text-primary-800 text-sm"
            >
              Ver detalhes →
            </button>
          </div>
        ))}
      </div>
      
      {equipamentos.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          Nenhum equipamento instalado
        </div>
      )}
    </div>
  );
};

const ChamadosList = ({ chamados, clienteId, onOpenChecklist }) => {
  const navigate = useNavigate();
  
  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'urgente': 'text-red-600',
      'alta': 'text-orange-600',
      'normal': 'text-blue-600',
      'baixa': 'text-gray-600'
    };
    return colors[prioridade] || 'text-gray-600';
  };
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Histórico de Chamados</h3>
          <button
            onClick={() => navigate('/chamados/novo', { state: { clienteId } })}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Novo Chamado
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Protocolo</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Título</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Prioridade</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Data</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {chamados.slice(0, 10).map((chamado) => (
              <tr key={chamado.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 text-sm font-medium">#{chamado.protocolo}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-sm">{chamado.titulo}</p>
                  <p className="text-xs text-gray-500 truncate max-w-xs">
                    {chamado.descricao}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <span className={`font-medium text-sm ${getPrioridadeColor(chamado.prioridade)}`}>
                    {chamado.prioridade}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    chamado.status === 'aberto' ? 'bg-blue-100 text-blue-800' :
                    chamado.status === 'em_andamento' ? 'bg-yellow-100 text-yellow-800' :
                    chamado.status === 'resolvido' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {chamado.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {format(new Date(chamado.createdAt), 'dd/MM/yyyy HH:mm')}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {chamado.checklist && chamado.checklist.length > 0 && onOpenChecklist && (
                      <button
                        onClick={() => onOpenChecklist(chamado, 'chamado')}
                        className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                        title="Gerenciar Checklist"
                      >
                        <ListChecks className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/chamados/${chamado.id}`)}
                      className="text-primary-600 hover:text-primary-800 text-sm"
                    >
                      Ver detalhes
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {chamados.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhum chamado encontrado
          </div>
        )}
      </div>
    </div>
  );
};

const AtendimentosList = ({ atendimentos, clienteId, onOpenChecklist }) => {
  const navigate = useNavigate();
  
  const getCanalIcon = (canal) => {
    switch (canal) {
      case 'telefone':
        return <Phone className="w-4 h-4 text-blue-500" />;
      case 'whatsapp':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'email':
        return <Mail className="w-4 h-4 text-gray-500" />;
      default:
        return <HeadphonesIcon className="w-4 h-4 text-gray-500" />;
    }
  };
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Histórico de Atendimentos</h3>
          <button
            onClick={() => navigate('/atendimentos/novo', { state: { clienteId } })}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Novo Atendimento
          </button>
        </div>
      </div>
      
      <div className="divide-y">
        {atendimentos.slice(0, 10).map((atendimento) => (
          <div key={atendimento.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
            <div className="flex items-start gap-4">
              <div className="mt-1">
                {getCanalIcon(atendimento.canal)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{atendimento.tipo?.nome}</p>
                  <span className="text-xs text-gray-500">
                    {format(new Date(atendimento.dataHora), 'dd/MM/yyyy HH:mm')}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mt-1">
                  {atendimento.descricao}
                </p>
                
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span>Duração: {atendimento.duracaoMinutos} min</span>
                  <span>Atendente: {atendimento.usuario?.nome}</span>
                  {atendimento.chamadoId && (
                    <span>Chamado: #{atendimento.chamado?.protocolo}</span>
                  )}
                </div>
                
                {/* Progresso do Checklist */}
                {atendimento.checklist && atendimento.checklist.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[150px]">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${atendimento.progressoPercentual || 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {atendimento.progressoPercentual || 0}%
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Botão de Checklist */}
                {atendimento.checklist && atendimento.checklist.length > 0 && (
                  <button
                    onClick={() => onOpenChecklist(atendimento)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Gerenciar Checklist"
                  >
                    <ListChecks className="w-5 h-5" />
                  </button>
                )}
                
                <button
                  onClick={() => navigate(`/atendimentos/${atendimento.id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm"
                >
                  Ver detalhes
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {atendimentos.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          Nenhum atendimento encontrado
        </div>
      )}
    </div>
  );
};

const OrdensServicoList = ({ ordensServico, clienteId, onOpenChecklist }) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Ordens de Serviço</h3>
          <button
            onClick={() => navigate('/ordens-servico/novo', { state: { clienteId } })}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Nova O.S.
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Protocolo</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Agendamento</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Técnico</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Progresso</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {ordensServico.map((os) => {
              const progresso = os.progressoPercentual || 0;
              const temChecklist = os.checklist && os.checklist.length > 0;
              
              return (
                <tr key={os.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 text-sm font-medium">{os.protocolo}</td>
                  <td className="px-4 py-3 text-sm capitalize">{os.tipo}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      os.status === 'concluida' ? 'bg-green-100 text-green-800' :
                      os.status === 'em_execucao' ? 'bg-yellow-100 text-yellow-800' :
                      os.status === 'agendada' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {os.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {os.dataAgendamento 
                      ? format(new Date(os.dataAgendamento), 'dd/MM/yyyy HH:mm')
                      : '-'
                    }
                  </td>
                  <td className="px-4 py-3 text-sm">{os.tecnico?.nome || '-'}</td>
                  <td className="px-4 py-3">
                    {temChecklist ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full transition-all"
                            style={{ width: `${progresso}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {progresso}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onOpenChecklist(os, 'ordem_servico')}
                        className="text-purple-600 hover:text-purple-800"
                        title="Gerenciar Checklist"
                      >
                        <ListChecks className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/ordens-servico/${os.id}`)}
                        className="text-primary-600 hover:text-primary-800 text-sm"
                      >
                        Ver detalhes
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {ordensServico.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhuma ordem de serviço encontrada
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteDetalhesCompleto;